import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import LeadCountCard from "../components/LeadCountCard";
import AddLeadModal from "../components/AddLeadModal";
import ActiveLeads from "../components/ActiveLeads";
import InvalidLeads from "../components/InvalidLeads";
import NotQualify from "../components/NotQualify";
import SearchBar from "../components/SearchBar";
import AddButton from "../components/AddButton";

import axios from "axios";
import API_BASE_URL from "../config/env";

import "../styles/Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"active" | "invalid" | "notQualify">("active");
  const [searchQuery, setSearchQuery] = useState("");

  const token = localStorage.getItem("authToken");

  const [leadCounts, setLeadCounts] = useState({
    active: 0,
    invalid: 0,
    notQualify: 0,
  });

  const [visitCounts, setVisitCounts] = useState({
    siteVisit: 0,
    f2f: 0,
    lead_scheduled: 0,
    invalid_count: 0,
    active_count: 0,
    total_count: 0,
    not_qualify: 0,
    transaction_done: 0,
  });
  
  const setLeadCountsRef = useRef(setLeadCounts);
  setLeadCountsRef.current = setLeadCounts;

  const handleLeadCountUpdate = useCallback(
    (type: "active" | "invalid" | "notQualify", count: number) => {
      setLeadCountsRef.current((prev) => {
        if (prev[type] === count) return prev;
        return { ...prev, [type]: count };
      });
    },
    []
  );

  const fetchVisitCounts = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/global-partner-connect/site-visit-count`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status === "success") {
        setVisitCounts({
          siteVisit: response.data.site_visit_count ?? 0,
          f2f: response.data.f2f_count ?? 0,
          active_count: response.data.active_count ?? 0,
          invalid_count: response.data.invalid_count ?? 0,
          lead_scheduled: response.data.lead_scheduled ?? 0,
          total_count: response.data.total_count ?? 0,
          not_qualify: response.data.not_qualify ?? 0,
          transaction_done: response.data.transaction_done ?? 0,
        });
      }
    } catch (error) {
      console.error("Error fetching visit counts:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchVisitCounts();
  }, [fetchVisitCounts]);

  const handleCardClick = (type: string) => {
    // Map the card types to the correct filter types
    let filterType = type;
    
    // Fix: Map "token" to "lead_scheduled" as that's what TableView expects
    if (type === "token") {
      filterType = "lead_scheduled";
    }
    
    navigate("/leads", { state: { filterType: filterType } });
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const renderLeadComponent = () => {
    switch (activeTab) {
      case "active":
        return (
          <ActiveLeads
            onCountUpdate={(count) => handleLeadCountUpdate("active", count)}
            searchQuery={searchQuery}
          />
        );
      case "invalid":
        return (
          <InvalidLeads
            onCountUpdate={(count) => handleLeadCountUpdate("invalid", count)}
            searchQuery={searchQuery}
          />
        );
      case "notQualify":
        return (
          <NotQualify
            onCountUpdate={(count) => handleLeadCountUpdate("notQualify", count)}
            searchQuery={searchQuery}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard">
      <Header />

      <div className="body-row">
        <Sidebar />

        <div className="main-content">
          <div className="width-set">
            {/* CARDS */}
            <div className="stats-row">
              <LeadCountCard count={visitCounts.total_count} title="Total Leads" onClick={() => handleCardClick("total_count")} clickable={true}/>
              <LeadCountCard count={visitCounts.active_count} title="Active Leads" onClick={() => handleCardClick("active_count")} clickable={true} />
              <LeadCountCard count={visitCounts.invalid_count} title="Invalid Leads" onClick={() => handleCardClick("invalid_count")} clickable={true} />
              <LeadCountCard count={visitCounts.not_qualify} title="Not Qualified Leads" onClick={() => handleCardClick("not_qualify")} clickable={true} />
              <LeadCountCard count={visitCounts.siteVisit} title="Site Visit Done" onClick={() => handleCardClick("siteVisit")} clickable={true}
              />
              <LeadCountCard
                count={visitCounts.f2f}
                title="F2F Done"
                onClick={() => handleCardClick("f2f")}
                clickable={true}
              />
              <LeadCountCard
                count={visitCounts.lead_scheduled}
                title="Scheduled"
                onClick={() => handleCardClick("lead_scheduled")} // Fixed: Changed from "token" to "lead_scheduled"
                clickable={true}
              />
              <LeadCountCard
                count={visitCounts.transaction_done}
                title="Transaction Done"
                onClick={() => handleCardClick("transaction_done")}
                clickable={true}
              />
            </div>

            <div className="search-add">
              <SearchBar
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                placeholder="Search leads by Customer Name..."
              />
              <AddButton onClick={() => setShowModal(true)} label="+ Add New Lead" />
            </div>

            <div className="lead-tabs">
              <button
                className={`lead-tab ${activeTab === "active" ? "active" : ""}`}
                onClick={() => setActiveTab("active")}
              >
                Active Leads
              </button>
              <button
                className={`lead-tab ${activeTab === "invalid" ? "active" : ""}`}
                onClick={() => setActiveTab("invalid")}
              >
                Invalid Leads
              </button>
              <button
                className={`lead-tab ${activeTab === "notQualify" ? "active" : ""}`}
                onClick={() => setActiveTab("notQualify")}
              >
                Not Qualified
              </button>
            </div>
            <h3 className="section-title">
              {activeTab === "active"
                ? `Active Leads (${leadCounts.active})`
                : activeTab === "invalid"
                ? `Invalid Leads (${leadCounts.invalid})`
                : `Not Qualified Leads (${leadCounts.notQualify})`}
            </h3>
            {renderLeadComponent()}
            {showModal && <AddLeadModal onClose={() => setShowModal(false)} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;